import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
} from '@mui/material';
import TableOrderLayout from '../../components/user/TableOrderLayout';

const PrivacyPage = () => {
  return (
    <TableOrderLayout 
      title="Privacy Policy" 
      subtitle="How we collect, use, and protect your information"
      showBackButton={true}
      showCart={false}
    >
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Paper elevation={2} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' } }}
          >
            Privacy Policy
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            gutterBottom
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
          
          <Divider sx={{ my: 3 }} />

          <Box sx={{ '& > *': { mb: { xs: 2, sm: 3 } } }}>
            <Box>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                1. Information We Collect
              </Typography>
              <Typography 
                variant="body2" 
                paragraph
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                We collect information you provide directly to us, such as when you create an account, make a reservation, place an order, or contact us for support. This may include:
              </Typography>
              <Typography 
                variant="body2" 
                component="ul" 
                sx={{ 
                  pl: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                <li>Name and contact information (email address, phone number)</li>
                <li>Reservation details (date, time, party size, special requests)</li>
                <li>Order information (items ordered, delivery address, payment information)</li>
                <li>Account credentials (username, password)</li>
                <li>Communication preferences</li>
              </Typography>
            </Box>

            <Box>
              <Typography 
                variant="h6" 
                fontWeight="bold" 
                gutterBottom
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                2. How We Use Your Information
              </Typography>
              <Typography 
                variant="body2" 
                paragraph
                sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
              >
                We use the information we collect to:
              </Typography>
              <Typography 
                variant="body2" 
                component="ul" 
                sx={{ 
                  pl: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                <li>Process and fulfill your orders and reservations</li>
                <li>Communicate with you about your orders, reservations, and our services</li>
                <li>Send you promotional materials and special offers (with your consent)</li>
                <li>Improve our website, products, and services</li>
                <li>Prevent fraud and ensure the security of our services</li>
                <li>Comply with legal obligations</li>
              </Typography>
            </Box>

            {[
              {
                title: "3. Information Sharing",
                content: "We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except in the following circumstances:",
                list: [
                  "With service providers who assist us in operating our website and conducting our business",
                  "When required by law or to protect our rights, property, or safety",
                  "In connection with a business transfer or acquisition",
                  "With your explicit consent"
                ]
              },
              {
                title: "4. Data Security",
                content: "We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure."
              },
              {
                title: "5. Cookies and Tracking Technologies",
                content: "We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie settings through your browser preferences, but disabling cookies may affect the functionality of our website."
              },
              {
                title: "6. Third-Party Links",
                content: "Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of these external sites. We encourage you to review the privacy policies of any third-party sites you visit."
              },
              {
                title: "7. Children's Privacy",
                content: "Our services are not directed to children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information."
              },
              {
                title: "8. Your Rights",
                content: "You have the right to:",
                list: [
                  "Access and update your personal information",
                  "Request deletion of your personal information",
                  "Opt-out of marketing communications",
                  "Request a copy of your data",
                  "Withdraw consent for data processing"
                ]
              },
              {
                title: "9. Data Retention",
                content: "We retain your personal information for as long as necessary to fulfill the purposes outlined in this privacy policy, unless a longer retention period is required or permitted by law."
              },
              {
                title: "10. Changes to This Policy",
                content: "We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the \"Last updated\" date."
              },
              {
                title: "11. Contact Us",
                content: "If you have any questions about this Privacy Policy, please contact us at:",
                additional: "Email: privacy@restaurant.com\nPhone: +1 (555) 123-4567\nAddress: 123 Main Street, City, State 12345"
              }
            ].map((section, index) => (
              <Box key={index + 2}>
                <Typography 
                  variant="h6" 
                  fontWeight="bold" 
                  gutterBottom
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                  {section.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  paragraph
                  sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                >
                  {section.content}
                </Typography>
                {section.list && (
                  <Typography 
                    variant="body2" 
                    component="ul" 
                    sx={{ 
                      pl: { xs: 2, sm: 3 },
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}
                  >
                    {section.list.map((item, idx) => (
                      <li key={idx}>{item}</li>
                    ))}
                  </Typography>
                )}
                {section.additional && (
                  <Typography 
                    variant="body2"
                    sx={{ 
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      whiteSpace: 'pre-line'
                    }}
                  >
                    {section.additional}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>
    </TableOrderLayout>
  );
};

export default PrivacyPage;
